/**
 * CommandDock - Navigation
 * ========================
 * Desktop: Left side vertical dock with logo
 * Mobile: Bottom tab bar (like mobile apps)
 */

import React, { useState, useEffect } from 'react';
import './styles.css';

// Import dragon logo
import dragonLogo from '../../assets/dravonix_icon.png';

const navItems = [
  {
    id: 'hero',
    label: 'Home',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    )
  },
  {
    id: 'protocol',
    label: 'About',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    )
  },
  {
    id: 'services',
    label: 'Services',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
      </svg>
    )
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
      </svg>
    )
  },
  {
    id: 'connect',
    label: 'Contact',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
      </svg>
    )
  }
];

const CommandDock = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isVisible, setIsVisible] = useState(false);

  // Handle navigation click
  const handleNavClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (item.isExternal) {
      window.open(item.href, '_blank');
      return;
    }
    
    const section = document.getElementById(item.id);
    
    if (section) {
      // Use scrollIntoView - works regardless of scroll container
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      
      setActiveSection(item.id);
    }
  };

  // Track scroll position to update active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      const sections = navItems.filter(item => !item.isExternal);
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section) {
          const sectionTop = section.offsetTop;
          if (scrollPosition >= sectionTop) {
            setActiveSection(sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    setTimeout(handleScroll, 100);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show dock after hero animation
  useEffect(() => {
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 4000);

    return () => clearTimeout(showTimer);
  }, []);

  return (
    <nav className={`command-dock ${isVisible ? 'visible' : ''}`}>
      <div className="dock-container">
        {/* Logo at top */}
        <div 
          className="dock-logo" 
          onClick={(e) => handleNavClick(e, { id: 'hero' })}
          role="button"
          tabIndex={0}
        >
          <img src={dragonLogo} alt="Dravonix" />
        </div>

        {/* Desktop: Top indicator */}
        <div className="dock-header">
          <div className="dock-indicator"></div>
        </div>

        {/* Navigation Items */}
        <ul className="dock-nav">
          {navItems.map(item => (
            <li key={item.id} className="dock-item">
              <button
                type="button"
                className={`dock-button ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => handleNavClick(e, item)}
                aria-label={item.label}
              >
                <span className="dock-icon">{item.icon}</span>
                <span className="dock-label">{item.label}</span>
                {activeSection === item.id && (
                  <span className="dock-active-bar"></span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {/* Desktop: Bottom line */}
        <div className="dock-footer">
          <div className="dock-line"></div>
        </div>
      </div>
    </nav>
  );
};

export default CommandDock;
